import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges
} from '@angular/core';
import { Shop } from '../../entities/shop';
import { CurrencyService } from '../../../core/services/currency.service';
import { BookingParametersInterface } from '../../entities/bookingParameters';
import { LocalizeRouterModule, LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { PartnerLinkService } from '../../../core/services/partner-link.service';
import { Router, RouterModule } from '@angular/router';
import { ShopDetailService } from '../../services/shop-detail.service';
import { AbTestService } from '@ab-test';
import { CommonModule } from '@angular/common';
import { CurrencyPipe } from '../../../core/pipes/currency.pipe';
import {
  IconComponent,
  provideIcons,
  adult,
  calendar,
  child,
  circleNotch,
  rightArrow,
  teen
} from '../../../shared/icons';
import { ExchangeCalculatorService } from '../../../shared/services/exchange-calculator.service';
import { PlatformInformationService } from '../../../../../projects/platform-manager/src/public_api';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AlpitranslatePipe } from '../../../core/pipes/alpitranslate.pipe';
import { CrowdinTranslationService } from '../../services/crowdin-translation.service';
import { loaderMap } from './translations/loader-map';

@Component({
  selector: 'app-proceed-to-product-list',
  templateUrl: './proceed-to-product-list.component.html',
  styleUrls: ['./proceed-to-product-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, RouterModule, LocalizeRouterModule, CurrencyPipe, IconComponent, AlpitranslatePipe],
  providers: [provideIcons({ circleNotch, rightArrow, calendar, adult, teen, child })]
})
export class ProceedToProductListComponent implements OnInit, OnChanges {
  @Input() shop: Shop;
  @Input() bookingParameters: BookingParametersInterface;
  @Input() adults: Array<number>;
  @Input() children: Array<number>;
  @Input() isLoadingNewPriceData = false;
  @Input() isLoadingNewOutOfTownPriceData = false;

  currencyCode = this.currencyService.getActualCurrency();
  productLink: string;

  constructor(
    protected currencyService: CurrencyService,
    protected localizedRouterService: LocalizeRouterService,
    protected partnerLinkService: PartnerLinkService,
    protected shopDetailService: ShopDetailService,
    protected router: Router,
    protected abTestService: AbTestService,
    protected cdr: ChangeDetectorRef,
    protected exchangeService: ExchangeCalculatorService,
    protected platformService: PlatformInformationService,
    protected destroyRef: DestroyRef,
    private crowdinTranslationService: CrowdinTranslationService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    this.cdr.markForCheck();
    if (changes['bookingParameters']) {
      this.bookingParameters = changes['bookingParameters'].currentValue;
      this.prepareProductLink();
    }
    if (changes['shop']) {
      this.prepareProductLink();
    }
  }

  getDifference(): string {
    return this.exchangeService.subtract(
      [this.shop.baseTurnoverAmount, this.shop.defaultProductOnlineAmount],
      this.currencyCode
    );
  }

  ngOnInit() {
    this.crowdinTranslationService.watchLangChange({
      loaderMap: loaderMap,
      destroyRef: this.destroyRef,
      successCallback: () => this.cdr.detectChanges()
    });
    this.prepareProductLink();

    this.currencyService.onCurrencyChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(currency => {
      this.currencyCode = currency;
      this.cdr.markForCheck();
    });

    this.localizedRouterService.routerEvents.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.prepareProductLink();
    });
  }

  private prepareProductLink() {
    if (this.bookingParameters) {
      const route = this.localizedRouterService.translateRoute(
        `/SKIHIREENTRY/${this.shop.townSlugs[this.localizedRouterService.parser.currentLang]}/${this.shop.id}/PRODUCTS`
      );
      const routeWithPartner = this.partnerLinkService.getUrlReplacingPartnerAfterPrefix(
        route,
        this.localizedRouterService.translateRoute('/SKIHIREENTRY')
      );

      const newLink = this.router
        .createUrlTree([routeWithPartner], {
          queryParams: this.bookingParameters.userDefinedSearch ? {} : { showDatepicker: '1' }
        })
        .toString();
      if (this.productLink != newLink) {
        this.productLink = newLink;
        this.cdr.markForCheck();
      }
    }
  }
}
